import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) { return res?.data?.data ?? res?.data ?? res; }
function arr(res: any): any[] { const d = pick(res); return Array.isArray(d) ? d : []; }

/** The report's filter set. Everything but the event is optional. */
export type EventReportArgs = {
  eventId: string | number;
  /** Free text over delegate, member and guest identity. */
  q?: string;
  status?: "paid" | "pending" | "free" | "";
  /**
   * Where the seat's money came from. 'induction' is a delegate registered by
   * settling an upgrade induction bundle — gateway-paid, but not at this
   * event's checkout, so it is excluded from 'online' rather than hidden
   * inside it. 'guest' selects guest access cards, which are not
   * registrations (a bundle-bought card still answers 'induction').
   */
  source?: "admin" | "online" | "self" | "induction" | "guest" | "";
  /** The admin who entered the registration or recorded its payment. */
  recorded_by?: string | number | "";
  /**
   * The delegate's own chapter, read off their member record. 'none' asks for
   * the delegates no chapter can claim — non-members and members never placed
   * in one — which is a real question and cannot be asked with a chapter id.
   */
  chapter?: string | "";
  /** Review cycle to measure against: an id, 'active', or 'none'. */
  diet?: string;
  /** Narrow to delegates in that cycle rather than merely tagging them. */
  in_diet?: boolean;
};

/**
 * Accept either a bare event id (the unfiltered report) or the full filter set,
 * and drop empty values.
 *
 * Empty values are dropped rather than sent blank because RTK Query keys its
 * cache on the argument: `{q: ""}` and `{}` ask the server the identical
 * question, and keeping both would fetch the same report twice.
 */
function normaliseReportArg(arg: string | number | EventReportArgs) {
  const raw: EventReportArgs = typeof arg === "object" ? arg : { eventId: arg };
  const out: Record<string, any> = { eventId: raw.eventId };

  if (raw.q?.trim())     out.q = raw.q.trim();
  if (raw.status)        out.status = raw.status;
  if (raw.source)        out.source = raw.source;
  if (raw.recorded_by)   out.recorded_by = String(raw.recorded_by);
  if (raw.chapter)       out.chapter = raw.chapter;
  if (raw.diet)          out.diet = raw.diet;
  if (raw.in_diet)       out.in_diet = 1;

  return out as { eventId: string | number } & Record<string, any>;
}

/** The same filters, as a query string the CSV download can be fetched with. */
export function eventReportQueryString(args: EventReportArgs, extra: Record<string, string> = {}) {
  const { eventId: _ignored, ...params } = normaliseReportArg(args);
  return new URLSearchParams({ ...params, ...extra } as Record<string, string>).toString();
}

export const adminEventsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({

    // ── Events CRUD ──────────────────────────────────────────────────────────
    adminGetEvents: b.query<any[], void>({
      query: () => "/admin/events",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "AdminEvents" as const, id: "LIST" }],
    }),

    adminGetEvent: b.query<any, string | number>({
      query: (id) => `/admin/events/${id}`,
      transformResponse: (r) => pick(r),
      providesTags: (_, __, id) => [{ type: "AdminEvents" as const, id }],
    }),

    adminCreateEvent: b.mutation<any, FormData>({
      query: (body) => ({ url: "/admin/events", method: "POST", body }),
      invalidatesTags: [{ type: "AdminEvents" as const, id: "LIST" }],
    }),

    adminUpdateEvent: b.mutation<any, { id: string | number; body: FormData }>({
      query: ({ id, body }) => ({ url: `/admin/events/${id}`, method: "POST", body }),
      invalidatesTags: (_, __, { id }) => [
        { type: "AdminEvents" as const, id: "LIST" },
        { type: "AdminEvents" as const, id },
      ],
    }),

    adminDeleteEvent: b.mutation<any, string | number>({
      query: (id) => ({ url: `/admin/events/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "AdminEvents" as const, id: "LIST" }],
    }),

    // ── Lifecycle ────────────────────────────────────────────────────────────
    adminPublishEvent: b.mutation<any, string | number>({
      query: (id) => ({ url: `/admin/events/${id}/publish`, method: "POST" }),
      invalidatesTags: (_, __, id) => [
        { type: "AdminEvents" as const, id: "LIST" },
        { type: "AdminEvents" as const, id },
      ],
    }),

    adminCancelEvent: b.mutation<any, string | number>({
      query: (id) => ({ url: `/admin/events/${id}/cancel`, method: "POST" }),
      invalidatesTags: (_, __, id) => [
        { type: "AdminEvents" as const, id: "LIST" },
        { type: "AdminEvents" as const, id },
      ],
    }),

    adminCompleteEvent: b.mutation<any, string | number>({
      query: (id) => ({ url: `/admin/events/${id}/complete`, method: "POST" }),
      invalidatesTags: (_, __, id) => [
        { type: "AdminEvents" as const, id: "LIST" },
        { type: "AdminEvents" as const, id },
      ],
    }),

    adminAllocateCpd: b.mutation<any, string | number>({
      query: (id) => ({ url: `/admin/events/${id}/allocate-cpd`, method: "POST" }),
      // Also the event's registrations: issuing stamps `cpd_awarded` on every
      // row it credits, and a second press has to see that to report honestly
      // rather than offering to issue the same CPD again.
      invalidatesTags: (_, __, id) => [
        { type: "AdminCpd" as const, id: "LIST" },
        { type: "EventRegistrations" as const, id },
      ],
    }),

    // ── Registrations ────────────────────────────────────────────────────────
    adminGetEventRegistrations: b.query<any[], string | number>({
      query: (id) => `/admin/events/${id}/registrations`,
      transformResponse: (r) => arr(r),
      providesTags: (_, __, id) => [{ type: "EventRegistrations" as const, id }],
    }),

    // ── Report ───────────────────────────────────────────────────────────────
    // Aggregates, the delegate rows and the guest list — every one of them
    // narrowed by the same filters, which are sent as query params.
    //
    // The rows come from the server rather than being filtered out of
    // adminGetEventRegistrations client-side: whether a delegate sits in the
    // current upgrade review cycle is a join the page cannot do, and totals and
    // rows computed from two different sources would eventually disagree.
    adminGetEventReport: b.query<any, string | number | EventReportArgs>({
      query: (arg) => {
        const { eventId, ...params } = normaliseReportArg(arg);
        return { url: `/admin/events/${eventId}/report`, params };
      },
      transformResponse: (r: any) => r?.data ?? r,
      // Recording a payment or marking attendance changes these figures, so the
      // report invalidates on the same tag as the registrations it summarises.
      providesTags: (_, __, arg) => [
        { type: "EventRegistrations" as const, id: normaliseReportArg(arg).eventId },
      ],
    }),

    adminSearchRegistrant: b.query<any[], { eventId: string | number; q: string }>({
      query: ({ eventId, q }) => ({
        url: `/admin/events/${eventId}/registrations/search`,
        params: { q },
      }),
      transformResponse: (r) => arr(r),
    }),

    // Find a member to register on their behalf.
    adminSearchEventMembers: b.query<any[], string>({
      query: (q) => ({ url: `/admin/events/members/search`, params: { q } }),
      transformResponse: (r) => arr(r),
    }),

    // Admin registers an attendee + records their (bank) payment (FormData).
    adminCreateRegistration: b.mutation<any, { eventId: string | number; body: FormData }>({
      query: ({ eventId, body }) => ({ url: `/admin/events/${eventId}/registrations`, method: "POST", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventRegistrations" as const, id: eventId }],
    }),

    /**
     * Bulk registration upload — dry run.
     *
     * Writes nothing: it parses the sheet and hands back a verdict per row, so
     * the desk sees who will be created and what will be charged before any
     * money is recorded. Deliberately NOT cache-invalidating, because nothing
     * on the server changed.
     */
    adminPreviewRegistrationImport: b.mutation<any, { eventId: string | number; body: FormData }>({
      query: ({ eventId, body }) => ({
        url: `/admin/events/${eventId}/registrations/import/preview`,
        method: "POST",
        body,
      }),
      transformResponse: (r: any) => r,
    }),

    /**
     * The same upload, for real. Creates the registrations that passed and
     * settles the fees of those marked paid — so, like recording a payment at
     * the desk, it moves the finance lists too.
     */
    adminImportRegistrations: b.mutation<any, { eventId: string | number; body: FormData }>({
      query: ({ eventId, body }) => ({
        url: `/admin/events/${eventId}/registrations/import`,
        method: "POST",
        body,
      }),
      transformResponse: (r: any) => r,
      invalidatesTags: (_, __, { eventId }) => [
        { type: "EventRegistrations" as const, id: eventId },
        "Payments" as const,
        "Receipts" as const,
      ],
    }),

    // Admin uploads/records an offline payment for an EXISTING pending registration (FormData).
    adminRecordRegistrationPayment: b.mutation<any, { eventId: string | number; registrationId: number; body: FormData }>({
      query: ({ eventId, registrationId, body }) => ({
        url: `/admin/events/${eventId}/registrations/${registrationId}/payment`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventRegistrations" as const, id: eventId }],
    }),

    /**
     * Settled registrations on this event that were never billed.
     *
     * Guest fees taken before payments could belong to a non-member were marked
     * paid on the registration and written nowhere else — no invoice, no
     * receipt, nothing in finance analysis.
     */
    adminGetMissingReceipts: b.query<any, string | number>({
      query: (eventId) => `/admin/events/${eventId}/receipts/missing`,
      transformResponse: (r: any) => r?.data ?? r,
      providesTags: (_, __, eventId) => [{ type: "EventRegistrations" as const, id: eventId }],
    }),

    /**
     * Raise the missing receipt(s) — one registration, or every outstanding one
     * on the event when `registration_id` is omitted.
     *
     * Invalidates the payments list as well as the event: this creates real
     * Payment rows, and finance analysis is showing a list that no longer
     * matches what is in the database.
     */
    adminBackfillEventReceipts: b.mutation<
      any,
      {
        eventId: string | number;
        registration_id?: number;
        bank_account_id?: number | string;
        payment_method?: string;
      }
    >({
      query: ({ eventId, ...body }) => ({
        url: `/admin/events/${eventId}/receipts/backfill`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { eventId }) => [
        { type: "EventRegistrations" as const, id: eventId },
        "Payments" as const,
        "Receipts" as const,
      ],
    }),

    // ── Guest cards ──────────────────────────────────────────────────────────
    //
    // People admitted on somebody else's account — a dignitary, a member's
    // spouse, a walk-in. NOT registrations: they hold no registration number,
    // no attendance, no materials and no CPD, which is why they have their own
    // endpoint rather than a flag on one.
    //
    // They are tagged as EventRegistrations all the same: issuing a card takes
    // money the event report counts, so the report has to refetch alongside.
    adminGetEventGuests: b.query<any[], string | number>({
      query: (eventId) => `/admin/events/${eventId}/guests`,
      transformResponse: (r) => arr(r),
      providesTags: (_, __, eventId) => [{ type: "EventRegistrations" as const, id: eventId }],
    }),

    // FormData — the desk may attach proof of payment, exactly as it can when
    // registering an attendee.
    adminCreateEventGuest: b.mutation<any, { eventId: string | number; body: FormData }>({
      query: ({ eventId, body }) => ({ url: `/admin/events/${eventId}/guests`, method: "POST", body }),
      // Issuing a paid card raises an invoice, a receipt and a ledger entry, so
      // the finance lists are stale too.
      invalidatesTags: (_, __, { eventId }) => [
        { type: "EventRegistrations" as const, id: eventId },
        "Payments" as const,
        "Receipts" as const,
      ],
    }),

    adminDeleteEventGuest: b.mutation<any, { eventId: string | number; guestId: number }>({
      query: ({ eventId, guestId }) => ({ url: `/admin/events/${eventId}/guests/${guestId}`, method: "DELETE" }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventRegistrations" as const, id: eventId }],
    }),

    // The attendee's own access card, reprintable from the desk — same payload
    // the member sees, photograph included. Answers 402 while the fee is
    // unsettled, exactly as the member-facing route does.
    adminGetRegistrationAccessCard: b.query<any, { eventId: string | number; registrationId: number }>({
      query: ({ eventId, registrationId }) =>
        `/admin/events/${eventId}/registrations/${registrationId}/access-card`,
      transformResponse: (r) => pick(r),
    }),

    // The attendee's certificate, reprintable from the desk — the route that
    // serves guests and admin-entered delegates, who have no portal login.
    adminGetRegistrationCertificate: b.query<any, { eventId: string | number; registrationId: number }>({
      query: ({ eventId, registrationId }) =>
        `/admin/events/${eventId}/registrations/${registrationId}/certificate`,
      transformResponse: (r) => pick(r),
    }),

    // The certificate designs the event form offers. Served rather than held
    // in the frontend so the names and signature blocks on the picker are the
    // ones the API will actually print.
    adminGetCertificateTemplates: b.query<any[], void>({
      query: () => "/admin/events/certificate-templates",
      transformResponse: (r) => arr(r),
    }),

    // ── Materials ────────────────────────────────────────────────────────────
    adminGetMaterials: b.query<any[], string | number>({
      query: (eventId) => `/admin/events/${eventId}/materials`,
      transformResponse: (r) => arr(r),
      providesTags: (_, __, eventId) => [{ type: "EventMaterials" as const, id: eventId }],
    }),

    adminCreateMaterial: b.mutation<any, { eventId: string | number; body: any }>({
      query: ({ eventId, body }) => ({ url: `/admin/events/${eventId}/materials`, method: "POST", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventMaterials" as const, id: eventId }],
    }),

    adminUpdateMaterial: b.mutation<any, { eventId: string | number; id: number; body: any }>({
      query: ({ eventId, id, body }) => ({ url: `/admin/events/${eventId}/materials/${id}`, method: "PUT", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventMaterials" as const, id: eventId }],
    }),

    adminDeleteMaterial: b.mutation<any, { eventId: string | number; id: number }>({
      query: ({ eventId, id }) => ({ url: `/admin/events/${eventId}/materials/${id}`, method: "DELETE" }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventMaterials" as const, id: eventId }],
    }),

    // ── Documents ────────────────────────────────────────────────────────────
    //
    // The FILES an event hands out — programme, papers, presentations,
    // communiqué. Distinct from Materials above, which is physical stock
    // counted and issued at a desk.
    //
    // Every write is multipart/form-data, so the body is a FormData the caller
    // builds; RTK Query passes it through untouched and the browser sets the
    // boundary. That is also why update is a POST — PHP does not parse
    // multipart on a PUT.
    adminGetEventDocuments: b.query<any[], string | number>({
      query: (eventId) => `/admin/events/${eventId}/documents`,
      transformResponse: (r) => arr(r),
      providesTags: (_, __, eventId) => [{ type: "EventDocuments" as const, id: eventId }],
    }),

    adminUploadEventDocument: b.mutation<any, { eventId: string | number; body: FormData }>({
      query: ({ eventId, body }) => ({ url: `/admin/events/${eventId}/documents`, method: "POST", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventDocuments" as const, id: eventId }],
    }),

    adminUpdateEventDocument: b.mutation<any, { eventId: string | number; id: number; body: FormData }>({
      query: ({ eventId, id, body }) => ({ url: `/admin/events/${eventId}/documents/${id}`, method: "POST", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventDocuments" as const, id: eventId }],
    }),

    adminDeleteEventDocument: b.mutation<any, { eventId: string | number; id: number }>({
      query: ({ eventId, id }) => ({ url: `/admin/events/${eventId}/documents/${id}`, method: "DELETE" }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventDocuments" as const, id: eventId }],
    }),

    // ── Refreshments ─────────────────────────────────────────────────────────
    adminGetRefreshments: b.query<any[], string | number>({
      query: (eventId) => `/admin/events/${eventId}/refreshments`,
      transformResponse: (r) => arr(r),
      providesTags: (_, __, eventId) => [{ type: "EventRefreshments" as const, id: eventId }],
    }),

    adminCreateRefreshment: b.mutation<any, { eventId: string | number; body: any }>({
      query: ({ eventId, body }) => ({ url: `/admin/events/${eventId}/refreshments`, method: "POST", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventRefreshments" as const, id: eventId }],
    }),

    adminUpdateRefreshment: b.mutation<any, { eventId: string | number; id: number; body: any }>({
      query: ({ eventId, id, body }) => ({ url: `/admin/events/${eventId}/refreshments/${id}`, method: "PUT", body }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventRefreshments" as const, id: eventId }],
    }),

    adminDeleteRefreshment: b.mutation<any, { eventId: string | number; id: number }>({
      query: ({ eventId, id }) => ({ url: `/admin/events/${eventId}/refreshments/${id}`, method: "DELETE" }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventRefreshments" as const, id: eventId }],
    }),

    // ── Scanner ──────────────────────────────────────────────────────────────
    scannerLookup: b.query<any, string>({
      query: (barcode) => `/admin/scanner/lookup/${barcode}`,
      transformResponse: (r) => pick(r),
    }),

    scannerMarkAttendance: b.mutation<any, { barcode: string; event_day_id: number }>({
      query: (body) => ({ url: "/admin/scanner/attendance", method: "POST", body }),
    }),

    scannerMarkRefreshment: b.mutation<any, { barcode: string; event_day_id: number }>({
      query: (body) => ({ url: "/admin/scanner/refreshment", method: "POST", body }),
    }),

    scannerUpdateMaterials: b.mutation<any, { registrationId: number; material_updates: { event_material_id: number; is_collected: boolean }[] }>({
      query: ({ registrationId, material_updates }) => ({
        url: `/admin/scanner/materials/${registrationId}`,
        method: "PUT",
        body: { material_updates },
      }),
    }),

    /**
     * Issue materials to one attendee from the event's materials desk.
     *
     * Distinct from `scannerUpdateMaterials`, which is the door's barcode-keyed
     * route held by scanner staff. This one is event-scoped and rides on
     * event.inventory.manage — the capability whoever runs the materials table
     * actually holds.
     *
     * Invalidates BOTH tags: issuing moves the event's stock (EventMaterials)
     * and changes what that registration has collected (EventRegistrations).
     * The scanner mutation invalidates neither, which is why stock counts on
     * the materials tab go stale after issuing from a registrant row.
     */
    adminIssueRegistrationMaterials: b.mutation<
      any,
      {
        eventId: string | number;
        registrationId: number;
        material_updates: { event_material_id: number; is_collected: boolean }[];
      }
    >({
      query: ({ eventId, registrationId, material_updates }) => ({
        url: `/admin/events/${eventId}/registrations/${registrationId}/materials`,
        method: "POST",
        body: { material_updates },
      }),
      invalidatesTags: (_, __, { eventId }) => [
        { type: "EventMaterials" as const, id: eventId },
        { type: "EventRegistrations" as const, id: eventId },
      ],
    }),

    adminAddMaterialStock: b.mutation<any, { eventId: string | number; id: number; quantity: number }>({
      query: ({ eventId, id, quantity }) => ({
        url: `/admin/events/${eventId}/materials/${id}/add-stock`,
        method: "POST",
        body: { quantity },
      }),
      invalidatesTags: (_, __, { eventId }) => [{ type: "EventMaterials" as const, id: eventId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAdminGetEventsQuery,
  useAdminGetEventQuery,
  useAdminCreateEventMutation,
  useAdminUpdateEventMutation,
  useAdminDeleteEventMutation,
  useAdminPublishEventMutation,
  useAdminCancelEventMutation,
  useAdminCompleteEventMutation,
  useAdminAllocateCpdMutation,
  useAdminGetEventRegistrationsQuery,
  useAdminGetEventReportQuery,
  useAdminSearchRegistrantQuery,
  useLazyAdminSearchEventMembersQuery,
  useAdminCreateRegistrationMutation,
  useAdminPreviewRegistrationImportMutation,
  useAdminImportRegistrationsMutation,
  useAdminRecordRegistrationPaymentMutation,
  useAdminGetEventGuestsQuery,
  useAdminCreateEventGuestMutation,
  useAdminDeleteEventGuestMutation,
  useAdminGetRegistrationAccessCardQuery,
  useAdminGetRegistrationCertificateQuery,
  useAdminGetCertificateTemplatesQuery,
  useAdminGetMissingReceiptsQuery,
  useAdminBackfillEventReceiptsMutation,
  useLazyAdminSearchRegistrantQuery,
  useAdminGetMaterialsQuery,
  useAdminCreateMaterialMutation,
  useAdminUpdateMaterialMutation,
  useAdminDeleteMaterialMutation,
  useAdminGetEventDocumentsQuery,
  useAdminUploadEventDocumentMutation,
  useAdminUpdateEventDocumentMutation,
  useAdminDeleteEventDocumentMutation,
  useAdminGetRefreshmentsQuery,
  useAdminCreateRefreshmentMutation,
  useAdminUpdateRefreshmentMutation,
  useAdminDeleteRefreshmentMutation,
  useScannerLookupQuery,
  useLazyScannerLookupQuery,
  useScannerMarkAttendanceMutation,
  useScannerMarkRefreshmentMutation,
  useScannerUpdateMaterialsMutation,
  useAdminIssueRegistrationMaterialsMutation,
  useAdminAddMaterialStockMutation,
} = adminEventsApi;
